import { useState, useEffect, useCallback } from "react";
import {
  usePermission,
  SystemPermissions,
} from "../../../../Utils/permissionAccess";
import {
  FiEdit2,
  FiFilter,
  FiPlus,
  FiRotateCcw,
  FiSearch,
} from "react-icons/fi";
import { useServices } from "../../useHook";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import GlobalPagination from "../../../../Components/Pagination/GlobalPagination";
import { IoEye } from "react-icons/io5";
import { MdDelete } from "react-icons/md";

const ServicesList = () => {
  const navigate = useNavigate();
  const { getServices, deleteService, searchServices } = useServices();

  const { hasPermission } = usePermission();
  const canRead = hasPermission(SystemPermissions.Services.READ);
  const canCreate = hasPermission(SystemPermissions.Services.CREATE);
  const canUpdate = hasPermission(SystemPermissions.Services.UPDATE);
  const canDelete = hasPermission(SystemPermissions.Services.DELETE);
  const canShowActions = canRead || canUpdate || canDelete;

  const [serviceData, setServiceData] = useState([]);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");

  const loadServices = useCallback(
    async (
      currentOffset = offset,
      currentLimit = limit,
      currentSearchFilter = searchFilter,
    ) => {
      setIsLoading(true);
      try {
        await getServices(
          setServiceData,
          setTotal,
          currentOffset,
          currentLimit,
          currentSearchFilter,
        );
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load services. Please refresh the page.",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [offset, limit, searchFilter, getServices],
  );

  useEffect(() => {
    loadServices(offset, limit);
  }, [offset, limit, loadServices]);

  const handlePageChange = (newOffset) => {
    setOffset(newOffset);
    loadServices(newOffset, limit);
  };

  const handleSearch = async () => {
    try {
      setIsLoading(true);
      setOffset(0);
      const response = await searchServices(searchFilter, 0, limit);
      setServiceData(response.data ?? []);
      const totalRecords =
        response.pagination?.totalRecords ?? response.data?.length ?? 0;
      setTotal(totalRecords);
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "There was an issue with the search.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchFilter("");
    setOffset(0);
    loadServices(0, limit, "");
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await deleteService(id);
        await loadServices(offset, limit);
        Swal.fire("Deleted!", "Service deleted successfully.", "success");
      } catch (error) {
        Swal.fire(
          "Error!",
          "Failed to delete service. Please try again.",
          "error",
        );
      }
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h2 className="text-2xl font-bold">Services</h2>

        {canCreate && (
          <button
            onClick={() => navigate("/services/create")}
            className="btn btn-primary h-[44px]"
          >
            <FiPlus className="text-white" />
            Add Service
          </button>
        )}
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by title"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>
        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleSearch}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            className="btn btn-reset-filter filter-btn"
            onClick={handleReset}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg  overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No.</th>
                <th className="table-th">Title</th>
                {canShowActions && <th className="table-th">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {serviceData.length > 0 ? (
                serviceData.map((service, index) => (
                  <tr
                    key={service.id}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">{offset + index + 1}</td>
                    <td className="table-td">{service.title}</td>

                    {canShowActions && (
                      <td className="table-td">
                        <div className="flex space-x-2">
                          {canRead && (
                            <button
                              className="btn btn-view "
                              onClick={() => navigate(`/services/${service.id}`)}
                            >
                              <IoEye className="text-white" /> View
                            </button>
                          )}
                          {canUpdate && (
                            <button
                              className="btn btn-edit text-white"
                              onClick={() =>
                                navigate(`/services/edit/${service.id}`)
                              }
                            >
                              <FiEdit2 className="text-white" /> Edit
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="btn btn-delete text-white"
                              onClick={() => handleDelete(service.id)}
                            >
                              <MdDelete className="text-white" /> Delete
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={canShowActions ? 3 : 2}
                    className="py-4 px-4 text-center text-gray-500 font-bold"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Loading services...
                      </div>
                    ) : (
                      "No services found"
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {serviceData.length > 0 && total > limit && (
        <div className="mt-4">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={total}
            onPageChange={handlePageChange}
            className="bg-white"
          />
        </div>
      )}
    </div>
  );
};

export default ServicesList;
